import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MainLayout } from "components/layout/MainLayout";
import {
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Input,
  Select,
  Button,
  ImageEditor,
  QRCodeDisplay,
  Checkbox,
  Modal,
} from "components/ui";
import { employeesService } from "services/employees.service";
import { countriesService } from "services/countries.service";
import { lookupService } from "services/lookup.service";
import { Language, Lookups } from "types/index";
import { useToast } from "hooks/useToast";
import { ROUTES, STORAGE_KEYS } from "constants/index";
import { appLocalStorage } from "utils/storage";
import { stripDataUrl } from "../../helpers/useConvertFileToBase64";
import { useFieldValidation } from "hooks/useFieldValidation";
import { Option } from "@/types/option";

const DIGITS_ONLY_REGEX = /^\d+$/;
 

export const EmployeeFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = Boolean(id) && !location.pathname.endsWith("/view");
  const isView = location.pathname.endsWith("/view");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<Option[]>([]);
  const { toast } = useToast();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    middleName: "",
    titleId: "",
    titleLookupId: "",
    email: "",
    emailLocalPart: "",
    mobileCountryId: "",
    mobileNumber: "",
    image: "",
    isActive: true,
  });
  const [companyDomain, setCompanyDomain] = useState<string | null>(null);
  const usesCompanyDomain = Boolean(companyDomain) && !isEdit;

  const validators = useMemo(
    () => ({
      firstName: (state: typeof form) =>
        !state.firstName?.trim() ? "First name is required" : undefined,
      lastName: (state: typeof form) =>
        !state.lastName?.trim() ? "Last name is required" : undefined,
      email: (state: typeof form) => {
        if (usesCompanyDomain) {
          return state.emailLocalPart?.trim()
            ? undefined
            : "Email is required";
        }
        return state.email?.trim() ? undefined : "Email is required";
      },
      titleId: (state: typeof form) =>
        !state.titleId ? "Title is required" : undefined,
      mobileCountryId: (state: typeof form) =>
        !state.mobileCountryId ? "Country code is required" : undefined,
      mobileNumber: (state: typeof form) => {
        if (!state.mobileNumber?.trim())
          return "Mobile number is required";
        if (!DIGITS_ONLY_REGEX.test(state.mobileNumber))
          return "Only digits allowed";
        return undefined;
      },
    }),
    [usesCompanyDomain]
  );

  const { errors, validateAll, revalidateFields, resetErrors } =
    useFieldValidation<typeof form>(validators);
  const [titles, setTitles] = useState<Option[]>([]);
  const [encryptedId, setEncryptedId] = useState<string | undefined>(undefined);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [modalForm, setModalForm] = useState({
    code: "",
    valueEn: "",
    valueAr: "",
  });
  const [modalErrors, setModalErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const items = await countriesService.getAll();
        setCountries(
          (items || []).map((c: any) => ({
            value: c.id,
            label: `${c.code} (${c.nameEN})`,
          }))
        );

        const lookupTitles = await lookupService.getByParentCode(
          Lookups.EmployeeTitles,
          Language.ENGLISH
        );
        setTitles(
          (lookupTitles || []).map((t: any) => ({
            value: (t.lookupId ??
              t.LookupId ??
              t.id ??
              t.Id ??
              t.code ??
              t.Code) as any,
            label: t.nameEN || t.NameEN || t.name || t.Name,
          }))
        );

        const storedDomain = appLocalStorage.get<string>(STORAGE_KEYS.DOMAIN);
        setCompanyDomain(storedDomain);

        if ((isEdit || isView) && id) {
          const emp = await employeesService.getById(id);
          setEncryptedId((emp as any).encryptedId);
          setForm({
            firstName: (emp as any).firstName || "",
            lastName: (emp as any).lastName || "",
            middleName: (emp as any).middleName || "",
            titleId: String((emp as any).titleId || ""),
            titleLookupId: String((emp as any).titleLookupId || ""),
            email: emp.email || "",
            emailLocalPart: emp.email?emp.email.replace(storedDomain || "", "") : "",
            mobileCountryId: String((emp as any).mobileCountryId || ""),
            mobileNumber: String((emp as any).mobileNumber || ""),
            image: emp.image ? `data:image/jpeg;base64,${emp.image}` : "",
            isActive: Boolean((emp as any).isActive),
          });
          resetErrors();
        }
      } catch (e: any) {
        const friendly =
          "An error occurred on the server. Please contact the administrator.";
        setError(e.message ?? friendly);
        toast.error(e.message ?? friendly);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, isEdit, resetErrors]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      const targetFields = name === "emailLocalPart" ? ["email"] : [name];
      revalidateFields(next, targetFields);
      return next;
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSelectChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    const { name, value } = e.target;
    if (name === "titleId") {
      const selected = titles.find((t) => String(t.value) === String(value));
      setForm((prev) => {
        const next = {
          ...prev,
          titleId: String(value),
          titleLookupId: String(selected?.value || ""),
        };
        revalidateFields(next, ["titleId"]);
        return next;
      });
      return;
    }
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      revalidateFields(next, [name]);
      return next;
    });
  };

  const handleCountryChange: React.ChangeEventHandler<HTMLSelectElement> = (
    e
  ) => {
    setForm((prev) => {
      const next = { ...prev, mobileCountryId: String(e.target.value) };
      revalidateFields(next, ["mobileCountryId"]);
      return next;
    });
  };

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D+/g, "");
    setForm((prev) => {
      const next = { ...prev, mobileNumber: digitsOnly };
      revalidateFields(next, ["mobileNumber"]);
      return next;
    });
  };

  const handleImageChange = (imageData: string | null) => {
    setForm((prev) => ({ ...prev, image: imageData || "" }));
  };

  // Modal handlers
  const handleModalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setModalForm(() => ({ code:value.trim().replace(" ","_").toLocaleUpperCase(), valueEn: value ,valueAr:value}));
    // Clear error when user starts typing
    if (modalErrors[name]) {
      setModalErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setModalForm({ code: "", valueEn: "", valueAr: "" });
    setModalErrors({});
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalForm({ code: "", valueEn: "", valueAr: "" });
    setModalErrors({});
  };

  const saveNewLookup = async () => {
    setIsModalLoading(true);
    setModalErrors({});
    
    try {
      const nextErrors: Record<string, string> = {};
      
      if (!modalForm.code?.trim()) nextErrors.code = "Code is required";
      if (!modalForm.valueEn?.trim()) nextErrors.valueEn = "English value is required";
      if (!modalForm.valueAr?.trim()) nextErrors.valueAr = "Arabic value is required";
      
      setModalErrors(nextErrors);
      
      if (Object.keys(nextErrors).length > 0) {
        setIsModalLoading(false);
        return;
      }

      // Get the parent lookup ID for EmployeeTitles
      const parentLookups = await lookupService.getAll();
      const employeeTitlesParent = parentLookups.find((l: any) => l.code === Lookups.EmployeeTitles);
      
      if (!employeeTitlesParent) {
        throw new Error("Employee Titles parent lookup not found");
      }

      const lookupDetails = [
        {
          id: 0,
          name: modalForm.valueEn,
          languageId: 1,
        },
        {
          id: 0,
          name: modalForm.valueAr,
          languageId: 2,
        },
      ];

      const payload = {
        code: modalForm.code,
        valueEn: modalForm.valueEn,
        valueAr: modalForm.valueAr,
        parentId: employeeTitlesParent.id,
        lookupDetails,
      } as any;
 
      await lookupService.create(payload);
       
      const lookupTitles = await lookupService.getByParentCode(
        Lookups.EmployeeTitles,
        Language.ENGLISH
      );
      setTitles(
        (lookupTitles || []).map((t: any) => ({
          value: (t.lookupId ??
            t.LookupId ??
            t.id ??
            t.Id ??
            t.code ??
            t.Code) as any,
          label: t.nameEN || t.NameEN || t.name || t.Name,
        }))
      );
      
      toast.success("New title added successfully");
      closeModal();
    } catch (e: any) {
      const friendly = "An error occurred while adding the new title. Please try again.";
      toast.error(e.message ?? friendly);
    } finally {
      setIsModalLoading(false);
    }
  };

  const onSubmit = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const validationResult = validateAll(form);
      if (Object.keys(validationResult).length > 0) {
        setIsLoading(false);
        return;
      }

      const imageBase64 =
        typeof form.image === "string" && form.image.startsWith("data:")
          ? stripDataUrl(form.image)
          : form.image || undefined;

      const emailToSend = (companyDomain && !isEdit)
        ? `${(form.emailLocalPart || '').trim()}${companyDomain}`
        : form.email;

      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        middleName: form.middleName,
        titleId: form.titleId ? Number(form.titleId) : undefined,
        titleLookupId: form.titleLookupId
          ? Number(form.titleLookupId)
          : undefined,
        email: emailToSend,
        mobileCountryId: form.mobileCountryId
          ? Number(form.mobileCountryId)
          : undefined,
        mobileNumber: form.mobileNumber ? Number(form.mobileNumber) : undefined,
        image: imageBase64,
        isActive: Boolean(form.isActive),
      } as any;

      if (isEdit && id) {
        await employeesService.update(id, payload);
        toast.success("Employee updated successfully");
      } else {
        await employeesService.create(payload);
        toast.success("Employee created successfully");
      }
      navigate(ROUTES.EMPLOYEES);
    } catch (e: any) {
      const friendly =
        "An error occurred on the server. Please contact the administrator.";
      setError(e.message ?? friendly);
      toast.error(e.message ?? friendly);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader
          title={
            isEdit ? "Edit Employee" : isView ? "View Employee" : "New Employee"
          }
        />
        <CardBody>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-6">
            <div className="sm:col-span-4">
              <div className="flex flex-col items-center space-y-6">
                <div className="flex justify-center">
                  <ImageEditor
                    currentImage={form.image}
                    onImageChange={handleImageChange}
                    className="max-w-md"
                  />
                </div>
                {(isEdit || isView) && id && (
                  <div className="flex justify-center">
                    {encryptedId ? (
                      <QRCodeDisplay
                        employeeKey={encryptedId}
                        employeeName={`${form.firstName} ${form.middleName} ${form.lastName}`.trim()}
                        size={128}
                        className="max-w-sm mx-auto"
                      />
                    ) : (
                      <div className="flex items-center justify-center p-6">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-gray-600">
                          Generating QR...
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="sm:col-span-8">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Personal Information
                </h3>
              </div>
              <Grid cols={2} gap="lg">
                <GridItem>
                  <Input
                    label="First Name"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    fullWidth
                    error={errors.firstName}
                    required
                    disabled={isView}
                  />
                </GridItem>
                <GridItem>
                  <Input
                    label="Last Name"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    fullWidth
                    error={errors.lastName}
                    required
                    disabled={isView}
                  />
                </GridItem>
                <GridItem>
                  <Input
                    label="Middle Name"
                    name="middleName"
                    value={form.middleName}
                    onChange={handleChange}
                    fullWidth
                    error={errors.middleName} 
                    disabled={isView}
                  />
                </GridItem>
                <GridItem>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Select
                        label="Title"
                        name="titleId"
                        options={titles}
                        value={form.titleId}
                        onChange={handleSelectChange}
                        fullWidth
                        placeholder="Select a title"
                        disabled={isView}
                        required
                        error={errors.titleId}
                      />
                    </div>
                    {!isView && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={openModal}
                        className="px-3 py-2 h-10"
                        title="Add new title"
                      >
                        +
                      </Button>
                    )}
                  </div>
                </GridItem>
                <GridItem>
                  {companyDomain ? (
                    <Input
                      label="Email"
                      placeholder="email"
                      value={form.emailLocalPart}
                      onChange={(e) => setForm((prev) => ({ ...prev, emailLocalPart: e.target.value,email: `${e.target.value.trim()}${companyDomain}` }))}
                      rightIcon={<span className="text-secondary-500">{companyDomain}</span>}
                      fullWidth
                      error={errors.email}
                      required
                      disabled={isView || isEdit}
                    />
                  ) : (
                    <Input
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      fullWidth
                      error={errors.email}
                      required
                      disabled={isView || isEdit}
                    />
                  )}
                </GridItem>
                <GridItem>
                  {isView ? (
                    <div className="h-full flex items-center">
                      <span
                        className={
                          `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ` +
                          (form.isActive
                            ? "bg-green-100 text-green-700 border-green-200"
                            : "bg-red-100 text-red-700 border-red-200")
                        }
                      >
                        {form.isActive ? "Active" : "Not Active"}
                      </span>
                    </div>
                  ) : (
                    <div className="h-full flex items-center">
                      <Checkbox
                        label="Active"
                        name="isActive"
                        checked={Boolean(form.isActive)}
                        onChange={handleCheckboxChange}
                        disabled={isView}
                      />
                    </div>
                  )}
                </GridItem>
                <GridItem colSpan={2}>
                  <div className="flex items-start gap-3">
                    <div className="w-44">
                      <Select
                        label="Country Code"
                        options={countries}
                        value={form.mobileCountryId}
                        onChange={handleCountryChange}
                        fullWidth
                        error={errors.mobileCountryId}
                        required
                        disabled={isView}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        label="Mobile Number"
                        name="mobileNumber"
                        type="tel"
                        inputMode="numeric"
                        pattern="\\d*"
                        value={form.mobileNumber}
                        onChange={handleMobileChange}
                        fullWidth
                        error={errors.mobileNumber}
                        required
                        disabled={isView}
                      />
                    </div>
                  </div>
                </GridItem>
              </Grid>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => navigate(ROUTES.EMPLOYEES)}
              disabled={isLoading}
            >
              Back
            </Button>
            {!isView && (
              <Button
                variant="primary"
                onClick={onSubmit}
                isLoading={isLoading}
                disabled={isLoading}
              >
                Save
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
 
      <Modal
        open={isModalOpen}
        title="Add New Title"
        onCancel={closeModal}
        onConfirm={saveNewLookup}
        confirmText="Save"
        cancelText="Cancel"
        isProcessing={isModalLoading}
      >
        <div className="space-y-4">
          <Input
            label="Title (English)"
            name="valueEn"
            value={modalForm.valueEn}
            onChange={handleModalChange}
            fullWidth
            error={modalErrors.valueEn}
            required
            placeholder="Enter title in English"
          />
        </div>
      </Modal>
    </MainLayout>
  );
};
